import {ReactElement, useEffect, useState} from 'react';

import {FeatureFlagContext} from './FeatureFlagContext';
import {useApiRead} from '../../hooks/useApiRead';
import {GetRoutes} from '../../lib/api/GET/GetRoutes';

const SUPPORTED_FLAGS = {
  MAINTENANCE_MODE: 'maintenance_mode',
  CHAT_BASED_TALENT_SURVEY_ENABLED: 'ai_chat_based_talent_survey_enabled',
  JOB_POSTING_PAGE: 'job_posting_page',
  INBOX_PAGE: 'inbox_page',
  WALKTHROUGHS_ENABLED: 'walkthroughs_enabled',
};

type FlagNames = keyof typeof SUPPORTED_FLAGS;

export type FeatureFlags = Record<FlagNames, boolean>;

const DEFAULT_VALUES: FeatureFlags = {
  MAINTENANCE_MODE: false,
  CHAT_BASED_TALENT_SURVEY_ENABLED: false,
  JOB_POSTING_PAGE: false,
  INBOX_PAGE: false,
  WALKTHROUGHS_ENABLED: true,
};

interface FeatureFlagResponse {
  name: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FeatureFlagContextValues {
  flags: FeatureFlags;
  isLoading: boolean;
}

interface Props {
  children: ReactElement;
}

export const FeatureFlagProvider = ({children}: Props) => {
  const [flags, setFlags] = useState<FeatureFlags>(DEFAULT_VALUES);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);

  const {data, isLoading, isPending, error} = useApiRead<FeatureFlagResponse[]>({
    path: GetRoutes.getFeatureFlags(),
    options: {refetchOnWindowFocus: true},
  });

  useEffect(() => {
    if (isPending || isLoading || error) return;
    const flags: FeatureFlags = {...DEFAULT_VALUES};

    (Object.keys(SUPPORTED_FLAGS) as FlagNames[]).forEach((key) => {
      const flag = data?.find((featureFlag) => featureFlag.name === SUPPORTED_FLAGS[key]);

      if (flag) {
        flags[key] = flag.enabled;
      }
    });

    setFlags(flags);
    setInitialLoading(false);
  }, [data, isLoading, error, isPending]);

  return (
    <FeatureFlagContext.Provider value={{flags, isLoading: initialLoading}}>{children}</FeatureFlagContext.Provider>
  );
};
